import { CarretArrowDown, CarretArrowUp, Cross } from '../../atoms/icons';
import styles from './ProductGallery.module.scss';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { Keyboard, Mousewheel, Pagination, Thumbs, Zoom } from 'swiper/modules';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';
import { useScrollLock } from '../../../utils/useLockScroll';
import { ImageWithOverlay } from '../../atoms/image/ImageWithOverlay';
import { theme } from './galleryViewPort/_vars';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

const LightBox = ({ zoomItem, images, onClose, alt, productOverlayUrl }) => {
  {/*@ts-ignore*/}
  const renderContext = useSelector((state) => state.renderContext) ?? {};
  const navigationTopRef = useRef(null);
  const navigationBottomRef = useRef(null);
  const imageRef = useRef(null);
  const thumbRef = useRef(null);
  const isMobile = useMedia('(max-width: 767px)', false);
  const [mainSwiper, setMainSwiper] = useState(null);
  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [activeThumbIndex, setActiveThumbIndex] = useState(null);
  const thumbImages = images.filter((img) => img.url);
  const [scrollLocked, setScrollLocked] = useState(false);
  const [onChanged, setOnChanged] = useState(false);

  useScrollLock(scrollLocked);

  const thumbSettings: SwiperProps = {
    freeMode: {
      enabled: true,
      sticky: false,
    },
    slidesPerView: 7,
    watchOverflow: true,
    watchSlidesProgress: true,
    modules: [Thumbs, Keyboard, Mousewheel, Zoom, Pagination],
    className: 'mySwiper',
    onSwiper: (swiper) => {
      setTimeout(() => {
        if (swiper.destroyed) {
          return;
        }

        //swiper.params.navigation.prevEl = navigationTopRef.current;
        //swiper.params.navigation.nextEl = navigationBottomRef.current;

        if (swiper.navigation) {
          swiper.navigation.destroy();
          swiper.navigation.init();
          swiper.navigation.update();
        }
      });
      setThumbsSwiper(swiper);
    },
    onSlideChange: () => setOnChanged(true),
    navigation: {
      prevEl: navigationTopRef.current,
      nextEl: navigationBottomRef.current,
    },
    breakpoints: {
      320: {
        direction: 'horizontal',
        spaceBetween: 5,
      },
      768: {
        direction: 'vertical',
        spaceBetween: 10,
      },
    },
  };

  useEffect(() => {
    if (mainSwiper && !mainSwiper.destroyed) {
      const { slides } = mainSwiper;
      let initialSlideIndex = 0;
      for (let i = 0, length = slides.length; i < length; i += 1) {
        const $slideEl = slides[i];
        if ($slideEl.id === zoomItem.mainImage.id) {
          initialSlideIndex = $slideEl.id === zoomItem.mainImage.id ? i : initialSlideIndex;
        }
      }
      mainSwiper.slideTo(initialSlideIndex);
      setScrollLocked(true);
    }
    return () => setScrollLocked(false);
  }, [mainSwiper, zoomItem]);

  if (renderContext.serverSideRendering) {
    return null;
  }

  const isActiveFirstImage = mainSwiper?.activeIndex === 0;
  const isActiveLastImage = mainSwiper?.activeIndex === images?.length - 1;

  const firstImageThumbIndex = thumbImages.findIndex((x) => !!x.url);
  const modifiedThumbArray = [...thumbImages].map((item, index) => {
    return {
      ...item,
      // @ts-ignore
      showOverlay: theme === 'jag' ? firstImageThumbIndex === index : true,
    };
  });

  const firstImageSlideIndex = images.findIndex((x) => !!x.url);
  const modifiedSlideArray = [...images].map((item, index) => {
    return {
      ...item,
      // @ts-ignore
      showOverlay: theme === 'jag' ? firstImageSlideIndex === index : true,
    };
  });

  // Use mobile and desktop thumbnail varsions
  const thumbs = (
    <div className={`ProductGallery__thumbsHolder ` + styles.thumbsHolder}>
      <div className={styles.innerWrapper}>
        <Swiper {...thumbSettings}>
          {modifiedThumbArray.map((image, idx) => {
            return (
              <SwiperSlide key={idx} id={`thumb_${image.id}`}>
                <div ref={thumbRef} className={styles.thumbImagesItem} key={image.id}>
                  <ImageWithOverlay
                    alt={alt}
                    itemProp="image"
                    className={styles.thumbImage}
                    image={image.url}
                    overlayUrl={image?.showOverlay && productOverlayUrl}
                    opts={{
                      crop: 'fill',
                      width: 102 * 2,
                      height: 140 * 2,
                      quality: 60,
                    }}
                    preloadImage={undefined}
                  />

                  {activeThumbIndex === idx && (
                    <div className={styles.thumbImagesItemActive}>
                      <div
                        className={styles.thumbInnerDiv}
                        style={{
                          width: `${thumbRef?.current?.clientHeight * 0.8}px`,
                          maxWidth: '100%',
                        }}
                      />
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
        {thumbImages.length > 7 && (
          <div className={styles.navigationBlock}>
            <div
              data-carousel-arrow="arrow_up"
              className={clsx(
                styles.arrowNavigationDeskTop,
                styles.arrowNavigationTop,
                isActiveFirstImage && !onChanged && styles.arrowDisabled,
              )}
              ref={navigationTopRef}
            >
              <CarretArrowUp />
            </div>
            <div
              data-carousel-arrow="arrow_down"
              className={clsx(
                styles.arrowNavigationDeskTop,
                styles.arrowNavigationBottom,
                isActiveLastImage && !onChanged && styles.arrowDisabled,
              )}
              ref={navigationBottomRef}
            >
              <CarretArrowDown />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Use mobile and desktop slides varsions
  const slides = useMemo(
    () =>
      !isMobile
        ? modifiedSlideArray.map((image) => {
            return (
              image.url && (
                <SwiperSlide key={image.id} id={image.id}>
                  <div className={styles.lightboxImagesItem}>
                    <ImageWithOverlay
                      alt={alt}
                      itemProp="image"
                      className={styles.image}
                      image={image.url}
                      overlayUrl={image?.showOverlay && productOverlayUrl}
                      opts={{
                        crop: 'fill',
                        width: 628 * 2,
                        height: 785 * 2,
                        quality: 80,
                        sirvWidth: 800,
                        sirvHeight: 1000,
                      }}
                      preloadImage={undefined}
                    />
                  </div>
                </SwiperSlide>
              )
            );
          })
        : modifiedSlideArray.map((image, idx) => {
            return (
              image.url && (
                <SwiperSlide key={idx} id={image.id} zoom={true}>
                  <ImageWithOverlay
                    alt={alt}
                    /*@ts-ignore*/
                    ref={imageRef}
                    itemProp="image"
                    className={styles.image}
                    overlayUrl={image?.showOverlay && productOverlayUrl}
                    image={image.url}
                    opts={{
                      crop: 'fill',
                      width: 628 * 2,
                      height: 785 * 2,
                      quality: 80,
                    }}
                    preloadImage={undefined}
                  />
                </SwiperSlide>
              )
            );
          }),
    [isMobile],
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className={`ProductGallery__lightboxWrapper ` + styles.lightboxWrapper}>
      <div className={`ProductGallery__lightboxImagesList ` + styles.lightboxImagesList}>
        <Swiper
          direction={!isMobile ? 'vertical' : 'horizontal'}
          onSlideChange={(swiper) => {
            setActiveThumbIndex(swiper.activeIndex);
            mainSwiper?.update();
          }}
          zoom={{
            maxRatio: 3,
            minRatio: 1,
          }}
          className="mySwiper2"
          mousewheel={!isMobile}
          initialSlide={3}
          navigation={!isMobile}
          modules={[Thumbs, Keyboard, Mousewheel, Zoom, Pagination]}
          thumbs={{
            swiper: thumbsSwiper,
          }}
          freeMode={{
            enabled: !isMobile,
            momentum: true,
            sticky: false,
          }}
          spaceBetween={0} // Set space between slides to 0
          breakpoints={{
            768: {
              slidesPerView: 'auto',
            },
          }}
          allowTouchMove={!isMobile}
          noSwiping={!!isMobile}
          pagination={{
            clickable: !isMobile,
          }}
          onSwiper={(swiper) => {
            setMainSwiper(swiper);
          }}
          observer
        >
          {slides}
        </Swiper>
      </div>
      {thumbs}
      <button className={styles.lightboxButton} onClick={() => onClose(null)}>
        <Cross width={'28'} height={'28'} />
      </button>
    </div>
  );
};

export default LightBox;
